import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, InboxIcon } from 'lucide-react';

/**
 * Reusable data table with sortable headers, pagination, and a horizontal-scroll
 * container. Designed as a drop-in for the existing hand-rolled tables.
 *
 * Column descriptor:
 * {
 *   key: string,                  // unique identifier (also default sortKey)
 *   label: string|ReactNode,      // header label
 *   sortable?: boolean,           // show sort affordance and call onSort
 *   sortKey?: string,             // override key sent to onSort (e.g. SQL column name)
 *   align?: 'left'|'right'|'center',
 *   width?: string|number,        // optional CSS width applied to <th> and <td>
 *   minWidth?: string|number,
 *   headerClassName?: string,
 *   cellClassName?: string,       // extra classes on <td>
 *   sticky?: 'left'|'right',      // sticky column (visible during horizontal scroll)
 *   render: (row, rowIndex) => ReactNode,
 * }
 *
 * Props:
 *  - columns, rows: data
 *  - rowKey: (row) => string|number
 *  - sortBy, sortDir, onSort: controlled sorting (server-side). If onSort is omitted
 *    the table sorts client-side on the cell value of `row[sortKey]`.
 *  - page, pageSize, total, onPageChange: controlled pagination (omit to disable)
 *  - loading: shows a loading row
 *  - emptyMessage: shown when rows.length === 0 and not loading
 *  - rowClassName: (row) => string
 *  - onRowClick: (row) => void
 *  - tableMinWidth: number, default 800
 */
export default function DataTable({
  columns,
  rows,
  rowKey,
  sortBy,
  sortDir = 'DESC',
  onSort,
  page,
  pageSize,
  total,
  onPageChange,
  loading = false,
  emptyMessage = 'No data',
  rowClassName,
  onRowClick,
  tableMinWidth,
  className = '',
}) {
  // Internal state for client-side sort when onSort is not provided.
  const [internalSort, setInternalSort] = useState({ key: sortBy || null, dir: sortDir });

  const effectiveSortKey = onSort ? sortBy : internalSort.key;
  const effectiveSortDir = onSort ? sortDir : internalSort.dir;

  const handleHeaderClick = (col) => {
    if (!col.sortable) return;
    const key = col.sortKey || col.key;
    if (onSort) {
      // Server-side: parent decides toggle behavior
      const nextDir = effectiveSortKey === key && effectiveSortDir === 'ASC' ? 'DESC' : 'ASC';
      onSort(key, nextDir);
    } else {
      setInternalSort((prev) => {
        if (prev.key !== key) return { key, dir: 'ASC' };
        return { key, dir: prev.dir === 'ASC' ? 'DESC' : 'ASC' };
      });
    }
  };

  // Apply client-side sort if no onSort handler.
  const displayRows = useMemo(() => {
    if (onSort || !internalSort.key) return rows;
    const dir = internalSort.dir === 'ASC' ? 1 : -1;
    const k = internalSort.key;
    return [...rows].sort((a, b) => {
      const av = a?.[k];
      const bv = b?.[k];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      // ISO date strings sort lexicographically; that's fine.
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [rows, onSort, internalSort]);

  const totalPages = pageSize && total != null ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const showPagination = totalPages != null && onPageChange && totalPages > 1;
  const fromRow = pageSize && page ? (page - 1) * pageSize + 1 : null;
  const toRow = pageSize && page && total != null ? Math.min(page * pageSize, total) : null;

  const minWidthStyle = tableMinWidth ? { minWidth: `${tableMinWidth}px` } : undefined;

  const stickyClasses = (col) => {
    if (col.sticky === 'left') return 'sticky left-0 z-10 bg-white group-hover:bg-gray-50';
    if (col.sticky === 'right') return 'sticky right-0 z-10 bg-white group-hover:bg-gray-50';
    return '';
  };
  const stickyHeadClasses = (col) => {
    if (col.sticky === 'left') return 'sticky left-0 z-20 bg-gray-50';
    if (col.sticky === 'right') return 'sticky right-0 z-20 bg-gray-50';
    return '';
  };

  const alignClass = (a) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200" style={minWidthStyle}>
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => {
                const sortKey = col.sortKey || col.key;
                const isSorted = effectiveSortKey === sortKey;
                const SortIcon = !col.sortable
                  ? null
                  : !isSorted
                    ? ChevronsUpDown
                    : effectiveSortDir === 'ASC'
                      ? ChevronUp
                      : ChevronDown;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={{
                      width: col.width,
                      minWidth: col.minWidth,
                    }}
                    className={[
                      'px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide select-none',
                      alignClass(col.align),
                      col.sortable ? 'cursor-pointer hover:bg-gray-100' : '',
                      stickyHeadClasses(col),
                      col.headerClassName || '',
                    ].join(' ')}
                    onClick={() => handleHeaderClick(col)}
                    aria-sort={isSorted ? (effectiveSortDir === 'ASC' ? 'ascending' : 'descending') : undefined}
                  >
                    <span
                      className={[
                        'inline-flex items-center gap-1',
                        col.align === 'right' ? 'flex-row-reverse' : '',
                        col.align === 'center' ? 'justify-center' : '',
                      ].join(' ')}
                    >
                      <span>{col.label}</span>
                      {SortIcon && (
                        <SortIcon className={`w-3.5 h-3.5 ${isSorted ? 'text-gray-700' : 'text-gray-400'}`} />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {columns.map((col, ci) => (
                    <td key={ci} className="px-3 py-3">
                      <div className="h-4 skeleton-shimmer rounded" style={{ width: ci === 0 ? '70%' : ci % 3 === 0 ? '40%' : '60%' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-12">
                  <div className="flex flex-col items-center justify-center text-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
                      <InboxIcon className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-500">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              displayRows.map((row, idx) => {
                const key = rowKey ? rowKey(row) : idx;
                const extraRowCls = rowClassName ? rowClassName(row) : '';
                return (
                  <tr
                    key={key}
                    className={`group hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''} ${extraRowCls}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{ width: col.width, minWidth: col.minWidth }}
                        className={[
                          'px-3 py-2 text-sm text-gray-900 align-middle',
                          alignClass(col.align),
                          col.cellClassName || '',
                          stickyClasses(col),
                        ].join(' ')}
                      >
                        {col.render(row, idx)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="grid grid-cols-3 items-center gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
          {/* Left: row info */}
          <div>
            {fromRow != null && toRow != null && total != null
              ? <>Showing <span className="font-medium text-gray-800">{fromRow}</span>–<span className="font-medium text-gray-800">{toRow}</span> of <span className="font-medium text-gray-800">{total}</span></>
              : null}
          </div>
          {/* Center: page navigation */}
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="px-2 font-medium text-gray-700">Page {page} of {totalPages}</span>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Right: empty spacer for balance */}
          <div />
        </div>
      )}
    </div>
  );
}
